import {
  type Asset,
  deriveVideoMode,
  estimateJobSec,
  framesToSeconds,
  type GenerationRequestInput,
  IMAGE_DEFAULT_NEGATIVE_PROMPT,
  IMAGE_MODEL_MAX_PIXELS,
  IMAGE_MODELS,
  IMAGE_SIZES,
  type ImageAspect,
  ImageModel,
  ImageRequest,
  imageDimensionOptions,
  imageModelHasPromptExperts,
  imageModelTakesNegativePrompt,
  imageModelTakesReferences,
  imageModelUpsamples,
  imageSizeFromSide,
  MAX_SPEECH_CHARS,
  MODEL_LOAD_SEC,
  maxFramesForSize,
  maxImageReferences,
  modelForRequest,
  SPEECH_LANGUAGE_LABELS,
  SPEECH_MODE_LABELS,
  SPEECH_SPEAKER_LABELS,
  SpeechLanguage,
  SpeechMode,
  SpeechRequest,
  SpeechSpeaker,
  type SpeedMode,
  VIDEO_FRAME_OPTIONS,
  VIDEO_MODE_LABELS,
  VIDEO_SIZES,
  type VideoAspect,
  type VideoQuality,
  VideoRequest,
  type VideoResolution,
  VOICE_REF_MAX_SEC,
  VOICE_REF_MIN_SEC,
} from "@studio/shared";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  AudioLines,
  ChevronDown,
  Clapperboard,
  Crop,
  Image as ImageIcon,
  ImageUpscale,
  Mic,
  Music2,
  Pencil,
  Plus,
  SlidersHorizontal,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { type Slot, useComposer, voiceClipBlocker } from "@/lib/composer";
import { isActiveJob, useLive } from "@/lib/events";
import { cn, formatWait } from "@/lib/format";
import type { CreateKind } from "@/router";
import { ImageCropEditor } from "./ImageCropEditor";
import { ImageMarkupEditor } from "./ImageMarkupEditor";
import { MediaThumb } from "./MediaThumb";
import { SlotPicker } from "./SlotPicker";
import { Button, Segmented, Sheet } from "./ui";

/** The kinds that are made from words; Enhance has a dock of its own (EnhanceDock). */
export type PromptKind = Exclude<CreateKind, "enhance">;

export const KIND_OPTIONS: { value: CreateKind; label: React.ReactNode; title: string }[] = [
  {
    value: "image",
    title: "Image",
    label: (
      <>
        <ImageIcon className="size-4" aria-hidden />
        <span className="sr-only">Image</span>
      </>
    ),
  },
  {
    value: "video",
    title: "Video",
    label: (
      <>
        <Clapperboard className="size-4" aria-hidden />
        <span className="sr-only">Video</span>
      </>
    ),
  },
  {
    value: "music",
    title: "Music",
    label: (
      <>
        <Music2 className="size-4" aria-hidden />
        <span className="sr-only">Music</span>
      </>
    ),
  },
  {
    value: "speech",
    title: "Speech",
    label: (
      <>
        <Mic className="size-4" aria-hidden />
        <span className="sr-only">Speech</span>
      </>
    ),
  },
  {
    value: "enhance",
    title: "Enhance: restore and enlarge a picture or a video",
    label: (
      <>
        <ImageUpscale className="size-4" aria-hidden />
        <span className="sr-only">Enhance</span>
      </>
    ),
  },
];

/** The mode as one word, for the dock slate and the settings button on a phone. */
const SPEECH_MODE_SHORT: Record<SpeechMode, string> = {
  speaker: "Packaged",
  clone: "Cloned",
  design: "Designed",
};

/** The second box under the prompt: the same field in every mode, asking for what that mode needs. */
const SPEECH_FIELDS: Record<SpeechMode, { label: string; placeholder: string }> = {
  speaker: { label: "How to say it", placeholder: "Optional — “cheerful and quick, like sharing good news”" },
  clone: {
    label: "What the clip says",
    placeholder: "Optional — type the words in the clip and the clone gets closer",
  },
  design: { label: "Who is speaking", placeholder: "An elderly British narrator, warm and slow" },
};

/** Ways of picking a voice, and packaged voices, the GPU servers can actually serve right now. */
function useSpeechOptions() {
  const capabilities = useQuery({ queryKey: ["capabilities"], queryFn: api.capabilities, staleTime: 60_000 });
  return {
    modes: capabilities.data?.speech.modes ?? SpeechMode.options,
    speakers: capabilities.data?.speech.speakers ?? SpeechSpeaker.options,
  };
}

/**
 * Time factor of the fast path an online GPU server has for a model (the share of the full time a fast run
 * takes), or null while none has one: then the dock neither offers nor sends a speed, and everything runs every
 * step as before.
 */
function useFastPath(model: string): number | null {
  const capabilities = useQuery({ queryKey: ["capabilities"], queryFn: api.capabilities, staleTime: 60_000 });
  return capabilities.data?.fast.find((f) => f.model === model)?.timeFactor ?? null;
}

/** The two ways to spend a diffusion model's steps, as the dock offers them where a server has a fast path. */
function speedOptions(
  stacked?: boolean,
): { value: SpeedMode; label: string; short: string; title: string }[] {
  return [
    {
      value: "fast",
      label: stacked ? "Fast" : "Fast (skips steps)",
      short: "Fast",
      title: "Skips about half of the steps: much quicker, and fine detail can come out a little softer",
    },
    {
      value: "quality",
      label: stacked ? "Full quality" : "Full quality (all steps)",
      short: "Full quality",
      title: "Runs every step: slower, keeps the finest detail",
    },
  ];
}

/** Picture checkpoints currently advertised by a live runner. */
function useImageOptions(): ImageModel[] {
  const capabilities = useQuery({ queryKey: ["capabilities"], queryFn: api.capabilities, staleTime: 60_000 });
  const models = capabilities.data?.image.models;
  return models && models.length > 0 ? models : ImageModel.options;
}

export function secondsLabel(frames: number): string {
  const s = framesToSeconds(frames);
  return s < 2 ? `${s.toFixed(1)} s` : `${Math.round(s * 2) / 2} s`.replace(".0 s", " s");
}

function useRequest(kind: PromptKind): GenerationRequestInput | null {
  const { image, video, music, speech, reuseSeed } = useComposer();
  const imageFast = useFastPath(image.model) !== null;
  const videoFast = useFastPath("minimax-h3") !== null;
  return useMemo(() => {
    // A seed only travels while the run it came from is being edited; otherwise every run is a fresh roll.
    const seedOf = (seed: number | null) => (reuseSeed && seed !== null ? seed : undefined);
    if (kind === "image") {
      return image.prompt.trim()
        ? {
            type: "image",
            model: image.model,
            prompt: image.prompt,
            // only a model that runs with guidance gets one; an emptied field means "none", not the default
            negativePrompt: imageModelTakesNegativePrompt(image.model) ? image.negativePrompt : undefined,
            aspect: image.aspect,
            width: image.width,
            height: image.height,
            count: image.count,
            referenceImageAssetIds: image.references.map((a) => a.id),
            seed: seedOf(image.seed),
            speed: imageFast ? image.speed : undefined,
            upsample: imageModelUpsamples(image.model) ? image.upsample !== false : undefined,
          }
        : null;
    }
    if (kind === "video") {
      if (!video.prompt.trim()) return null;
      return {
        type: "video",
        prompt: video.prompt,
        resolution: video.resolution,
        aspect: video.aspect,
        frames: video.frames,
        quality: video.quality,
        firstFrameAssetId: video.firstFrame?.id,
        lastFrameAssetId: video.lastFrame?.id,
        referenceImageAssetIds: video.references.filter((a) => a.kind === "image").map((a) => a.id),
        referenceVideoAssetIds: video.references.filter((a) => a.kind === "video").map((a) => a.id),
        referenceAudioAssetIds: video.references.filter((a) => a.kind === "audio").map((a) => a.id),
        seed: seedOf(video.seed),
        speed: videoFast ? video.speed : undefined,
      };
    }
    if (kind === "music") {
      if (!music.style.trim()) return null;
      return {
        type: "music",
        style: music.style,
        lyrics: music.withLyrics && !music.instrumental ? music.lyrics : "",
        instrumental: music.instrumental,
        durationSec: music.durationSec,
        seed: seedOf(music.seed),
      };
    }
    if (!speech.text.trim()) return null;
    const clone = speech.mode === "clone";
    return {
      type: "speech",
      mode: speech.mode,
      text: speech.text,
      speaker: speech.speaker,
      // A saved voice carries its own reference audio; a clip picked in the dock is cloned as it is.
      voiceId: clone && speech.voiceId ? speech.voiceId : undefined,
      voiceRefAssetId: clone && !speech.voiceId ? speech.voiceRef?.id : undefined,
      referenceText: clone ? speech.referenceText : "",
      instruct: clone ? "" : speech.instruct,
      language: speech.language,
      seed: seedOf(speech.seed),
    };
  }, [kind, image, video, music, speech, reuseSeed, imageFast, videoFast]);
}

/**
 * Time until the result, as the person experiences it: jobs ahead, a model switch, the work itself (shorter when
 * it runs fast, by the fast path's time factor).
 */
function useEstimate(request: GenerationRequestInput | null, fastFactor: number | null): number | null {
  const { jobs, gpu } = useLive();
  if (!request) return null;
  const parsed = request.type === "video" ? VideoRequest.safeParse(request) : null;
  if (parsed && !parsed.success) return null;
  const full = request.type === "video" && parsed?.success ? parsed.data : (request as never);
  const model = modelForRequest(full);
  const ahead = Object.values(jobs)
    .filter(isActiveJob)
    .reduce((sum, job) => sum + (job.etaSec ?? 0), 0);
  const load = gpu?.activeModel === model ? 0 : (MODEL_LOAD_SEC[model] ?? 30);
  const speed = request.type === "image" || request.type === "video" ? request.speed : undefined;
  const factor = speed === "fast" && fastFactor !== null ? fastFactor : 1;
  return ahead + load + Math.round(estimateJobSec(full) * factor);
}

const PLACEHOLDERS: Record<string, string> = {
  image: "Describe a picture — “a red fox curled up in fresh snow, soft morning light”",
  image_edit: "Describe the change — “replace the object marked in red with a vase of wildflowers”",
  text: "Describe the scene, the action and the camera — sound is created too",
  first_frame: "What happens next? Describe the motion and the camera",
  last_frame: "How does the video arrive at this frame?",
  first_last: "How does it move from the first frame to the last?",
  references: "Describe the video; mention who or what from your references appears",
  music: "Describe the song — genre, mood, instruments, voice",
  speech: "Type what should be said out loud — punctuation shapes the pauses",
};

/** What the big box holds, for screen readers. */
const FIELD_LABELS: Record<PromptKind, string> = {
  image: "Prompt",
  video: "Prompt",
  music: "Song description",
  speech: "What to say",
};

const EDIT_NOUN: Record<PromptKind, string> = {
  image: "a picture",
  video: "a video",
  music: "a song",
  speech: "a recording",
};

export function PromptDock({ kind }: { kind: PromptKind }) {
  const navigate = useNavigate();
  const composer = useComposer();
  const { image, video, music, speech } = composer;
  const request = useRequest(kind);
  const fastFactor = useFastPath(kind === "image" ? image.model : "minimax-h3");
  const estimate = useEstimate(request, fastFactor);
  const imageModels = useImageOptions();
  const { modes: speechModes, speakers: speechSpeakers } = useSpeechOptions();
  const [picker, setPicker] = useState<Slot | null>(null);
  const [imagePicker, setImagePicker] = useState(false);
  const [marking, setMarking] = useState<Asset | null>(null);
  const [cropping, setCropping] = useState<{ asset: Asset; kind: "image" | "video" } | null>(null);
  const [voicePicker, setVoicePicker] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);

  // Never offer a voice the fleet cannot serve: a draft saved when a checkpoint was around falls back.
  useEffect(() => {
    if (kind !== "speech") return;
    const mode = speechModes[0];
    if (mode && !speechModes.includes(speech.mode)) composer.setSpeech({ mode });
    const speaker = speechSpeakers[0];
    if (speaker && !speechSpeakers.includes(speech.speaker)) composer.setSpeech({ speaker });
  }, [kind, speechModes, speechSpeakers, speech.mode, speech.speaker, composer]);

  // A draft can outlive the runner that served its checkpoint. Keep the choice runnable.
  useEffect(() => {
    if (kind !== "image" || imageModels.includes(image.model)) return;
    const model = imageModels[0];
    if (model)
      composer.setImage({ model, references: imageModelTakesReferences(model) ? image.references : [] });
  }, [kind, imageModels, image.model, image.references, composer]);

  const mode = deriveVideoMode({
    firstFrameAssetId: video.firstFrame?.id,
    lastFrameAssetId: video.lastFrame?.id,
    referenceImageAssetIds: video.references.filter((a) => a.kind === "image").map((a) => a.id),
    referenceVideoAssetIds: video.references.filter((a) => a.kind === "video").map((a) => a.id),
    referenceAudioAssetIds: video.references.filter((a) => a.kind === "audio").map((a) => a.id),
  });

  const prompt =
    kind === "image"
      ? image.prompt
      : kind === "video"
        ? video.prompt
        : kind === "music"
          ? music.style
          : speech.text;
  const setPrompt = (value: string) =>
    kind === "image"
      ? composer.setImage({ prompt: value })
      : kind === "video"
        ? composer.setVideo({ prompt: value })
        : kind === "music"
          ? composer.setMusic({ style: value })
          : composer.setSpeech({ text: value });

  const draftSeed =
    kind === "image"
      ? image.seed
      : kind === "video"
        ? video.seed
        : kind === "music"
          ? music.seed
          : speech.seed;
  const clearSeed = () =>
    kind === "image"
      ? composer.setImage({ seed: null })
      : kind === "video"
        ? composer.setVideo({ seed: null })
        : kind === "music"
          ? composer.setMusic({ seed: null })
          : composer.setSpeech({ seed: null });

  const enhance = async () => {
    if (kind !== "image" || !imageModelHasPromptExperts(image.model) || !image.prompt.trim() || enhancing)
      return;
    setEnhancing(true);
    try {
      const improved = await api.enhancePrompt({
        prompt: image.prompt,
        referenceImageAssetIds: image.references.map((asset) => asset.id),
      });
      let aspect = improved.aspect;
      if (aspect === null && improved.ratioFollow !== null) {
        const source = image.references[improved.ratioFollow];
        if (source?.width && source.height) {
          const ratio = source.width / source.height;
          aspect = (Object.keys(IMAGE_SIZES) as ImageAspect[]).reduce((best, candidate) => {
            const size = IMAGE_SIZES[candidate];
            const bestSize = IMAGE_SIZES[best];
            return Math.abs(size.width / size.height - ratio) <
              Math.abs(bestSize.width / bestSize.height - ratio)
              ? candidate
              : best;
          }, "1:1");
        }
      }
      composer.setImage({
        prompt: improved.prompt,
        ...(aspect ? { aspect, ...IMAGE_SIZES[aspect] } : {}),
      });
      toast.success("Prompt improved");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setEnhancing(false);
    }
  };

  // Grow the prompt box with its text, up to a third of the screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text changes
  useEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.32)}px`;
  }, [prompt, kind]);

  const place = (asset: Asset, slot: Slot) => {
    const note = composer.placeInVideo(asset, slot);
    if (note) toast(note);
  };

  const addImageReference = (asset: Asset) => {
    const note = composer.addImageReference(asset);
    if (note) toast.error(note);
  };

  /** Clone from a clip picked or dropped right here, without saving it as a voice first. */
  const cloneFromClip = (asset: Asset) => {
    const blocker =
      asset.kind === "audio" ? voiceClipBlocker(asset.durationSec) : "A voice is cloned from a sound file.";
    if (blocker) {
      toast.error(blocker);
      return;
    }
    composer.setSpeech({ mode: "clone", voiceRef: asset, voiceId: null });
  };

  /** Dropped or pasted files: a first picture becomes the start frame, the rest become references. */
  const addFiles = async (files: File[]) => {
    const media = files.filter((file) => /^(image|video|audio)\//.test(file.type));
    if (media.length === 0) {
      toast.error("Paste or drop a picture, a clip or a sound.");
      return;
    }
    if (kind === "image") {
      const pictures = media.filter((file) => file.type.startsWith("image/"));
      if (pictures.length !== media.length) {
        toast.error("An image edit takes pictures, not clips or sounds.");
        return;
      }
      // a dropped picture turns a model that cannot edit into Qwen (see addImageReference), so count for that one
      const editModel = imageModelTakesReferences(image.model) ? image.model : "qwen-image-2.1";
      const room = maxImageReferences(editModel) - image.references.length;
      if (pictures.length > room) {
        toast.error(
          `${IMAGE_MODELS[editModel].label} works from at most ${maxImageReferences(editModel)} pictures in one edit.`,
        );
        return;
      }
      const pending = toast.loading(
        pictures.length === 1 ? "Adding the picture…" : `Adding ${pictures.length} pictures…`,
      );
      try {
        for (const file of pictures) addImageReference(await api.upload(file));
      } catch (error) {
        toast.error((error as Error).message);
      } finally {
        toast.dismiss(pending);
      }
      return;
    }
    // On the speech tab a sound is the voice to clone, not something a video should use.
    if (kind === "speech" && media[0]?.type.startsWith("audio/")) {
      if (!speechModes.includes("clone")) {
        toast.error("No GPU server can clone a voice right now.");
        return;
      }
      const clip = toast.loading("Adding the clip…");
      try {
        cloneFromClip(await api.upload(media[0]));
      } catch (error) {
        toast.error((error as Error).message);
      } finally {
        toast.dismiss(clip);
      }
      return;
    }
    const pending = toast.loading(media.length === 1 ? "Adding the file…" : `Adding ${media.length} files…`);
    try {
      for (const file of media) {
        const asset = await api.upload(file);
        const current = useComposer.getState().video;
        place(
          asset,
          asset.kind === "image" && !current.firstFrame && current.references.length === 0
            ? "first"
            : "reference",
        );
      }
      if (kind !== "video") {
        await navigate({ to: "/create", search: { kind: "video" } });
        toast("Added to a video: pictures and clips can start or guide one.");
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      toast.dismiss(pending);
    }
  };

  const dropzone = useDropzone({
    noClick: true,
    noKeyboard: true,
    // pasting is handled below for every kind (the dropzone's own handler would add each file twice)
    noPaste: true,
    disabled: kind !== "image" && kind !== "video" && kind !== "speech",
    onDrop: (files) => void addFiles(files),
  });

  const submit = async () => {
    if (!request || busy) return;
    if (request.type === "image") {
      const parsed = ImageRequest.safeParse(request);
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? "Check the image settings.");
        return;
      }
    }
    if (request.type === "video") {
      const parsed = VideoRequest.safeParse(request);
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? "Check the video settings.");
        return;
      }
    }
    if (request.type === "speech") {
      const parsed = SpeechRequest.safeParse(request);
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? "Check the voice settings.");
        return;
      }
    }
    setBusy(true);
    try {
      const job = await api.createJob({ request, parentJobId: composer.parentJobId ?? undefined });
      useLive.getState().upsertJob(job);
      composer.setParent(null);
      // The bar that shows the seed is gone now, so the seed goes too: nothing should travel unseen.
      clearSeed();
      composer.setReuseSeed(true);
      document.getElementById("run-feed")?.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sizes = VIDEO_SIZES[video.resolution][video.aspect];
  const maxFrames = maxFramesForSize(sizes.width, sizes.height);
  // Keep the chosen length valid when the size changes.
  useEffect(() => {
    if (video.frames > maxFrames)
      composer.setVideo({ frames: VIDEO_FRAME_OPTIONS.filter((f) => f <= maxFrames).at(-1) ?? 73 });
  }, [maxFrames, video.frames, composer]);

  const settingsSummary =
    kind === "image"
      ? `${IMAGE_MODELS[image.model].label} · ${image.width}×${image.height} · ×${image.count}`
      : kind === "video"
        ? `${video.resolution === "2k" ? "2K" : video.resolution} · ${secondsLabel(video.frames)}${fastFactor !== null && video.speed === "fast" ? " · fast" : ""}`
        : kind === "music"
          ? `${music.durationSec < 60 ? `${music.durationSec} s` : `${Math.round(music.durationSec / 6) / 10} min`}${music.instrumental ? " · instrumental" : ""}`
          : `${speech.mode === "speaker" ? SPEECH_SPEAKER_LABELS[speech.speaker].title : SPEECH_MODE_SHORT[speech.mode]} · ${SPEECH_LANGUAGE_LABELS[speech.language]}`;

  // The line on the right of the dock names the mode of the kinds that have one.
  const modeLabels =
    kind === "video" ? VIDEO_MODE_LABELS[mode] : kind === "speech" ? SPEECH_MODE_LABELS[speech.mode] : null;

  return (
    <div
      {...dropzone.getRootProps()}
      className="relative mx-auto w-full max-w-[880px] overflow-x-clip px-3 pb-3 md:px-6 md:pb-4"
    >
      <input {...dropzone.getInputProps()} />
      <div
        onPaste={(e) => {
          const files = Array.from(e.clipboardData.files);
          if (files.length === 0) return; // plain text pastes as usual
          e.preventDefault();
          void addFiles(files);
        }}
        className={cn(
          "rounded-[var(--radius-dock)] border border-line bg-panel shadow-[0_14px_40px_-22px_rgba(0,0,0,0.7)] transition-colors",
          dropzone.isDragActive && "border-film",
        )}
      >
        {/* Editing a finished run fills the dock from it, which is invisible once the toast is gone — and it
            persists across reloads. Say so, and give it a way out that does not mean emptying every field. */}
        {composer.parentJobId ? (
          <div className="flex items-center gap-2 border-b border-line px-3 py-1.5 text-[13px] text-mute">
            <span className="min-w-0 flex-1 truncate">Editing {EDIT_NOUN[kind]} you made.</span>
            {draftSeed !== null ? (
              <button
                type="button"
                onClick={() => composer.setReuseSeed(!composer.reuseSeed)}
                title={
                  composer.reuseSeed
                    ? "Keeping the same seed. Click to roll a new one instead."
                    : "Rolling a new seed. Click to keep the one it was made with."
                }
                className={cn(
                  "tabular shrink-0 rounded-[8px] border px-2 py-0.5 text-[12px] font-medium",
                  composer.reuseSeed
                    ? "border-line bg-raised text-paper"
                    : "border-transparent text-mute hover:bg-raised",
                )}
              >
                {composer.reuseSeed ? `Seed ${draftSeed}` : "New seed"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                composer.clear(kind);
                composer.setParent(null);
                composer.setReuseSeed(true);
                toast("Started fresh");
              }}
              title="Start fresh"
              className="-mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-[8px] text-mute hover:bg-raised hover:text-paper"
            >
              <span className="sr-only">Stop editing and start fresh</span>
              <X className="size-4" />
            </button>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 px-3 pt-2.5 md:flex-row md:items-start md:gap-3">
          {kind === "image" ? (
            <ImageSlots
              onPick={() => setImagePicker(true)}
              onMark={setMarking}
              onCrop={(asset) => setCropping({ asset, kind: "image" })}
              onRemove={composer.removeImageReference}
            />
          ) : null}
          {kind === "video" ? (
            <VideoSlots
              onPick={setPicker}
              onCrop={(asset) => setCropping({ asset, kind: "video" })}
              onRemove={composer.removeFromVideo}
            />
          ) : null}
          {kind === "speech" && speech.mode === "clone" ? (
            <VoiceSlot onPickClip={() => setVoicePicker(true)} />
          ) : null}
          <div className="relative block min-w-0 flex-1 md:py-1">
            <label htmlFor="generation-prompt" className="sr-only">
              {FIELD_LABELS[kind]}
            </label>
            <textarea
              id="generation-prompt"
              ref={textarea}
              rows={1}
              value={prompt}
              maxLength={kind === "speech" ? MAX_SPEECH_CHARS : undefined}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder={
                kind === "video"
                  ? PLACEHOLDERS[mode]
                  : kind === "image" && image.references.length
                    ? PLACEHOLDERS.image_edit
                    : PLACEHOLDERS[kind]
              }
              className={cn(
                "scrollbar-thin block w-full resize-none bg-transparent text-[16px] leading-6 text-paper outline-none placeholder:text-mute/80",
                kind === "image" &&
                  (imageModelHasPromptExperts(image.model) || imageModelUpsamples(image.model)) &&
                  "pr-10",
              )}
            />
            {kind === "image" && imageModelUpsamples(image.model) ? (
              <button
                type="button"
                onClick={() => composer.setImage({ upsample: image.upsample === false })}
                aria-pressed={image.upsample !== false}
                aria-label="Prompt upsampler"
                title={
                  image.upsample !== false
                    ? "Prompt upsampler on: FLUX.3's own upsampler expands the prompt before drawing. Click to send the prompt as typed."
                    : "Prompt upsampler off: the prompt goes as typed. Black Forest Labs say results are weaker without it."
                }
                className={cn(
                  "absolute right-0 top-0 inline-flex size-8 items-center justify-center rounded-[9px] hover:bg-raised md:top-1",
                  image.upsample !== false ? "text-film" : "text-mute opacity-60",
                )}
              >
                <WandSparkles className="size-4" aria-hidden />
              </button>
            ) : null}
            {kind === "image" && imageModelHasPromptExperts(image.model) ? (
              <button
                type="button"
                onClick={() => void enhance()}
                disabled={!image.prompt.trim() || enhancing}
                aria-label="Improve prompt"
                title="Improve prompt with Qwen's image prompt expert"
                className="absolute right-0 top-0 inline-flex size-8 items-center justify-center rounded-[9px] text-film hover:bg-raised disabled:cursor-not-allowed disabled:text-mute disabled:opacity-50 md:top-1"
              >
                <WandSparkles className={cn("size-4", enhancing && "animate-pulse")} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        {kind === "music" && music.withLyrics && !music.instrumental ? (
          <label className="mx-3 mt-2 block rounded-[var(--radius-control)] border border-line bg-desk/60 px-3 py-2">
            <span className="text-[13px] font-medium text-mute">Lyrics</span>
            <textarea
              rows={4}
              value={music.lyrics}
              onChange={(e) => composer.setMusic({ lyrics: e.target.value })}
              placeholder={
                "[Verse]\nMorning light on the kitchen floor\n\n[Chorus]\nAnd the winter turns to gold"
              }
              className="scrollbar-thin mt-1 block w-full resize-y bg-transparent font-sans text-[15px] leading-6 outline-none placeholder:text-mute/70"
            />
          </label>
        ) : null}

        {kind === "image" && imageModelTakesNegativePrompt(image.model) ? (
          <label className="mx-3 mt-2 block rounded-[var(--radius-control)] border border-line bg-desk/60 px-3 py-2">
            <span className="flex items-center justify-between gap-2 text-[13px] font-medium text-mute">
              Avoid
              {image.negativePrompt !== IMAGE_DEFAULT_NEGATIVE_PROMPT ? (
                <button
                  type="button"
                  onClick={() => composer.setImage({ negativePrompt: IMAGE_DEFAULT_NEGATIVE_PROMPT })}
                  className="rounded-[7px] px-1.5 text-[12px] hover:bg-raised hover:text-paper"
                >
                  Reset to the realism default
                </button>
              ) : null}
            </span>
            <textarea
              rows={2}
              value={image.negativePrompt}
              onChange={(e) => composer.setImage({ negativePrompt: e.target.value })}
              placeholder="Nothing to avoid. Add what should not appear: extra legs, plastic skin, text…"
              className="scrollbar-thin mt-1 block w-full resize-y bg-transparent font-sans text-[14px] leading-5 outline-none placeholder:text-mute/70"
            />
          </label>
        ) : null}

        {kind === "speech" ? (
          <label className="mx-3 mt-2 block rounded-[var(--radius-control)] border border-line bg-desk/60 px-3 py-2">
            <span className="text-[13px] font-medium text-mute">{SPEECH_FIELDS[speech.mode].label}</span>
            <textarea
              rows={2}
              value={speech.mode === "clone" ? speech.referenceText : speech.instruct}
              onChange={(e) =>
                composer.setSpeech(
                  speech.mode === "clone" ? { referenceText: e.target.value } : { instruct: e.target.value },
                )
              }
              placeholder={SPEECH_FIELDS[speech.mode].placeholder}
              className="scrollbar-thin mt-1 block w-full resize-y bg-transparent font-sans text-[15px] leading-6 outline-none placeholder:text-mute/70"
            />
          </label>
        ) : null}

        <div className="flex items-center gap-1.5 px-2 pb-2 pt-2 sm:gap-2">
          <Segmented
            size="sm"
            className="shrink-0"
            value={kind}
            onChange={(value) => navigate({ to: "/create", search: { kind: value } })}
            options={KIND_OPTIONS}
          />
          <div className="hidden min-w-0 items-center gap-1 md:flex">
            <Settings kind={kind} maxFrames={maxFrames} />
          </div>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-label={`Settings: ${settingsSummary}`}
            className="tabular inline-flex h-8 min-w-0 shrink-0 items-center gap-1 whitespace-nowrap rounded-[9px] px-1.5 max-[340px]:shrink text-[13px] font-medium text-mute hover:bg-raised hover:text-paper md:hidden"
          >
            <SlidersHorizontal className="size-4 shrink-0" aria-hidden />
            <span className="max-[389px]:hidden max-[420px]:truncate">{settingsSummary}</span>
          </button>
          <p
            className="tabular ml-auto hidden min-w-0 truncate text-right text-[13px] text-mute sm:block"
            title={modeLabels?.hint}
          >
            {modeLabels ? (
              <span className="hidden font-medium text-paper lg:inline">{modeLabels.title}</span>
            ) : null}
            {modeLabels && estimate ? <span className="hidden lg:inline"> · </span> : null}
            {estimate ? `about ${formatWait(estimate)}` : null}
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={!request || busy}
            className="ml-auto min-w-[64px] shrink-0 px-3 sm:ml-0 sm:min-w-[96px]"
          >
            {busy ? "Starting…" : "Create"}
          </Button>
        </div>
      </div>

      {picker ? (
        <SlotPicker
          open
          onOpenChange={(open) => !open && setPicker(null)}
          title={picker === "first" ? "Start frame" : picker === "last" ? "End frame" : "Add a reference"}
          description={
            picker === "reference"
              ? "People, objects, places, clips or sounds the video should use. Up to 9 images, 3 clips and 3 sounds."
              : picker === "first"
                ? "The video starts on this image."
                : "The video ends on this image."
          }
          kinds={picker === "reference" ? ["image", "video", "audio"] : ["image"]}
          onPick={(asset) => place(asset, picker)}
        />
      ) : null}

      {imagePicker ? (
        <SlotPicker
          open
          onOpenChange={setImagePicker}
          title="Picture to edit or follow"
          description={`${IMAGE_MODELS[image.model].label} can work from up to ${maxImageReferences(image.model) || 10} pictures. After adding one, use its pencil button to mark a specific region.`}
          kinds={["image"]}
          onPick={addImageReference}
        />
      ) : null}

      <ImageMarkupEditor
        asset={marking}
        open={marking !== null}
        onOpenChange={(open) => !open && setMarking(null)}
        onSaved={(marked) => {
          if (marking) composer.replaceImageReference(marking.id, marked);
          setMarking(null);
        }}
      />

      <ImageCropEditor
        asset={cropping?.asset ?? null}
        open={cropping !== null}
        onOpenChange={(open) => !open && setCropping(null)}
        onSaved={(crop) => {
          if (!cropping) return;
          const blocker = composer.addCloseUp(cropping.kind, cropping.asset.id, crop);
          if (blocker) {
            toast.error(blocker);
          } else if (cropping.kind === "video") {
            const pictures = useComposer.getState().video.references.filter((a) => a.kind === "image");
            const n = pictures.findIndex((a) => a.id === crop.id) + 1;
            const of = pictures.findIndex((a) => a.id === cropping.asset.id) + 1;
            toast.success(`Close-up added as Picture ${n}`, {
              description: `Say what it shows in the prompt, e.g. "<Picture ${n}> is a close-up of the label in <Picture ${of}>: keep its text exact."`,
            });
          } else {
            toast.success("Close-up added", { description: "Say in the prompt what the close-up shows." });
          }
          setCropping(null);
        }}
      />

      {voicePicker ? (
        <SlotPicker
          open
          onOpenChange={setVoicePicker}
          title="Voice to clone"
          description={`A clip of one person speaking, ${VOICE_REF_MIN_SEC} to ${VOICE_REF_MAX_SEC} seconds long. Save it as a voice in Settings to use it again.`}
          kinds={["audio"]}
          onPick={cloneFromClip}
        />
      ) : null}

      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen} title="Settings">
        <div className="flex flex-col gap-5">
          <Settings kind={kind} maxFrames={maxFrames} stacked />
        </div>
      </Sheet>
    </div>
  );
}

/** Pictures Qwen edits or follows, with a pencil for spatial marks and a crop for close-ups. */
function ImageSlots({
  onPick,
  onMark,
  onCrop,
  onRemove,
}: {
  onPick: () => void;
  onMark: (asset: Asset) => void;
  onCrop: (asset: Asset) => void;
  onRemove: (id: string) => void;
}) {
  const { image } = useComposer();
  return (
    <div className="scrollbar-none -mx-1 flex shrink-0 items-center gap-1.5 overflow-x-auto px-1 py-0.5 md:max-w-[46%] md:flex-wrap md:overflow-visible">
      {image.references.map((asset) => (
        <SlotTile
          key={asset.id}
          label="Reference"
          name="image reference"
          asset={asset}
          onAdd={() => {}}
          onMark={() => onMark(asset)}
          onCrop={() => onCrop(asset)}
          onRemove={onRemove}
        />
      ))}
      {image.references.length < (maxImageReferences(image.model) || 10) ? (
        <button
          type="button"
          onClick={onPick}
          title="Add a picture to edit or use as visual direction"
          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-[9px] border border-dashed border-line px-2 text-[13px] font-medium text-mute hover:border-mute hover:text-paper"
        >
          <Plus className="size-3.5" aria-hidden />
          {image.references.length ? (
            <span className="tabular">
              {image.references.length}/{maxImageReferences(image.model) || 10}
            </span>
          ) : (
            "Edit image"
          )}
        </button>
      ) : null}
    </div>
  );
}

/** Frames or references of a video: small chips beside the prompt, a thumbnail once something is chosen. */
function VideoSlots({
  onPick,
  onCrop,
  onRemove,
}: {
  onPick: (slot: Slot) => void;
  onCrop: (asset: Asset) => void;
  onRemove: (id: string) => void;
}) {
  const { video } = useComposer();
  const framesMode = video.references.length === 0;
  // H3 numbers the reference pictures <Picture 1>, <Picture 2>… in this order; the badge lets a prompt name them
  const pictureNumber = (asset: Asset) =>
    video.references.filter((a) => a.kind === "image").findIndex((a) => a.id === asset.id) + 1;
  return (
    <div className="scrollbar-none -mx-1 flex shrink-0 items-center gap-1.5 overflow-x-auto px-1 py-0.5 md:max-w-[46%] md:flex-wrap md:overflow-visible">
      {framesMode ? (
        <>
          <SlotTile
            label="Start"
            name="start frame"
            asset={video.firstFrame}
            onAdd={() => onPick("first")}
            onRemove={onRemove}
          />
          <SlotTile
            label="End"
            name="end frame"
            asset={video.lastFrame}
            onAdd={() => onPick("last")}
            onRemove={onRemove}
          />
        </>
      ) : null}
      {video.references.map((asset) => (
        <SlotTile
          key={asset.id}
          label={asset.kind === "image" ? `Picture ${pictureNumber(asset)}` : "Reference"}
          name="reference"
          asset={asset}
          badge={asset.kind === "image" ? String(pictureNumber(asset)) : undefined}
          onAdd={() => {}}
          onCrop={asset.kind === "image" ? () => onCrop(asset) : undefined}
          onRemove={onRemove}
        />
      ))}
      {!video.firstFrame && !video.lastFrame ? (
        <button
          type="button"
          onClick={() => onPick("reference")}
          title="People, objects, places, clips or sounds the video should use"
          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-[9px] border border-dashed border-line px-2 text-[13px] font-medium text-mute hover:border-mute hover:text-paper"
        >
          <Plus className="size-3.5" aria-hidden />
          {video.references.length ? (
            <span className="tabular">{video.references.length}/12</span>
          ) : (
            "References"
          )}
        </button>
      ) : null}
    </div>
  );
}

export function SlotTile({
  label,
  name,
  asset,
  badge,
  onAdd,
  onMark,
  onCrop,
  onRemove,
}: {
  label: string;
  name: string;
  asset: Asset | null;
  /** A short number shown on the tile (the <Picture N> of a video reference). */
  badge?: string;
  onAdd: () => void;
  onMark?: () => void;
  onCrop?: () => void;
  onRemove: (id: string) => void;
}) {
  if (!asset) {
    return (
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Add a ${name}`}
        className="inline-flex h-8 shrink-0 items-center gap-1 rounded-[9px] border border-dashed border-line px-2 text-[13px] font-medium text-mute hover:border-mute hover:text-paper"
      >
        <Plus className="size-3.5" aria-hidden />
        {label}
      </button>
    );
  }
  return (
    <div className="group relative size-8 shrink-0" title={asset.kind === "audio" ? "Sound" : `${label}`}>
      <MediaThumb asset={asset} className="size-full rounded-[7px]" edge={false} />
      {asset.kind === "audio" ? (
        <AudioLines className="pointer-events-none absolute inset-0 m-auto size-4 text-paper" aria-hidden />
      ) : null}
      {onMark ? (
        <button
          type="button"
          onClick={onMark}
          className="absolute -bottom-1.5 -left-1.5 inline-flex size-[18px] items-center justify-center rounded-full border border-line bg-film text-film-ink hover:bg-[#f7cf4f]"
          aria-label="Draw on this picture to mark the area to change"
          title="Mark an area"
        >
          <Pencil className="size-3" />
        </button>
      ) : null}
      {onCrop ? (
        <button
          type="button"
          onClick={onCrop}
          className="absolute -bottom-1.5 -right-1.5 inline-flex size-[18px] items-center justify-center rounded-full border border-line bg-raised text-paper hover:bg-line"
          aria-label="Crop a close-up of a detail in this picture"
          title="Crop a close-up"
        >
          <Crop className="size-3" />
        </button>
      ) : null}
      {badge ? (
        <span className="tabular pointer-events-none absolute -left-1 -top-1 inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-film px-1 text-[10px] font-semibold leading-none text-film-ink">
          {badge}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => onRemove(asset.id)}
        className="absolute -right-1.5 -top-1.5 inline-flex size-[18px] items-center justify-center rounded-full border border-line bg-raised text-paper hover:bg-line"
        aria-label={`Remove the ${name}`}
      >
        <X className="size-3" />
      </button>
    </div>
  );
}

// Entries of the voice list that are not a saved voice: the clip used once, and the two ways to get one.
// The colons keep them apart from any voice id.
const CLIP_OPTION = "::clip";
const PICK_OPTION = "::pick";
const MANAGE_OPTION = "::manage";

/** Clone mode: whose voice speaks — one you saved, or a clip used just this once. */
function VoiceSlot({ onPickClip }: { onPickClip: () => void }) {
  const composer = useComposer();
  const { speech } = composer;
  const navigate = useNavigate();
  const voices = useQuery({ queryKey: ["voices"], queryFn: api.voices, staleTime: 60_000 });
  const chosen = speech.voiceId ?? (speech.voiceRef ? CLIP_OPTION : "");
  const options = [
    ...(chosen ? [] : [{ value: "", label: "Pick a voice" }]),
    ...(voices.data ?? []).map((voice) => ({ value: voice.id, label: voice.name })),
    ...(speech.voiceRef
      ? [{ value: CLIP_OPTION, label: speech.voiceRef.name ?? "The clip you picked", short: "This clip" }]
      : []),
    { value: PICK_OPTION, label: "Use an audio clip…" },
    { value: MANAGE_OPTION, label: "Save and rename voices…" },
  ];
  return (
    <div className="-mx-1 flex shrink-0 items-center px-1 py-0.5 md:max-w-[46%]">
      <SlateSelect
        label="Voice to clone"
        value={chosen}
        options={options}
        className="border border-line"
        onChange={(value) => {
          if (value === PICK_OPTION) onPickClip();
          else if (value === MANAGE_OPTION) void navigate({ to: "/account" });
          else if (value === CLIP_OPTION) composer.setSpeech({ voiceId: null });
          else composer.setSpeech({ voiceId: value || null, voiceRef: null });
        }}
      />
    </div>
  );
}

/** A setting as its current value, like a slate: opens the native list, so it is compact and works on every device. */
export function SlateSelect<T extends string | number>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: T;
  /** `short` is what the slate shows once chosen, when the line in the list is too long for it. */
  options: { value: T; label: string; short?: string; disabled?: boolean; title?: string }[];
  onChange: (value: T) => void;
  className?: string;
}) {
  const chosen = options.find((o) => o.value === value);
  const current = chosen?.short ?? chosen?.label ?? String(value);
  return (
    <label
      title={chosen?.title}
      className={cn(
        "relative inline-flex h-8 min-w-0 max-w-full shrink-0 items-center rounded-[9px] text-[13px] font-medium text-paper hover:bg-raised has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-film",
        className,
      )}
    >
      <span className="sr-only">{label}</span>
      <span aria-hidden className="tabular pointer-events-none flex min-w-0 items-center gap-1 pl-2 pr-1.5">
        <span className="min-w-0 truncate">{current}</span>
        <ChevronDown className="size-3.5 text-mute" />
      </span>
      <select
        value={String(value)}
        onChange={(e) => {
          const picked = options.find((o) => String(o.value) === e.target.value);
          if (picked) onChange(picked.value);
        }}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0"
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)} disabled={o.disabled} title={o.title}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const VIDEO_RESOLUTIONS: { value: VideoResolution; label: string }[] = [
  { value: "480p", label: "480p" },
  { value: "720p", label: "720p" },
  { value: "1080p", label: "1080p" },
  { value: "2k", label: "2K" },
];
const VIDEO_DETAIL: { value: VideoQuality; label: string }[] = [
  { value: "fast", label: "Draft" },
  { value: "standard", label: "Standard" },
  { value: "high", label: "Fine" },
];
const MUSIC_LENGTHS = [30, 60, 120, 180].map((s) => ({
  value: s,
  label: s < 60 ? `${s} s` : `${s / 60} min`,
}));
const MUSIC_VOCALS = [
  { value: "auto", label: "Sung" },
  { value: "lyrics", label: "My lyrics" },
  { value: "inst", label: "Instrumental" },
];

/**
 * The settings of a kind: compact slate values in the dock (stacked = false), labelled segmented rows in the
 * settings sheet on phones (stacked = true).
 */
function Settings({ kind, maxFrames, stacked }: { kind: PromptKind; maxFrames: number; stacked?: boolean }) {
  const composer = useComposer();
  const { image, video, music, speech } = composer;
  const imageModels = useImageOptions();
  const { modes, speakers } = useSpeechOptions();
  const imageFast = useFastPath(image.model) !== null;
  const videoFast = useFastPath("minimax-h3") !== null;

  const setting = <T extends string | number>(
    label: string,
    value: T,
    options: { value: T; label: string; short?: string; disabled?: boolean; title?: string }[],
    onChange: (value: T) => void,
  ) =>
    stacked ? (
      <div key={label} className="flex flex-col gap-2">
        <span className="text-sm font-medium text-mute">{label}</span>
        <div className="flex flex-wrap gap-2">
          <Segmented
            size="sm"
            value={value}
            onChange={onChange}
            options={options}
            className="max-w-full flex-wrap"
          />
        </div>
      </div>
    ) : (
      <SlateSelect key={label} label={label} value={value} options={options} onChange={onChange} />
    );

  /** A setting with too many choices for a row of buttons: a slate everywhere, labelled in the sheet. */
  const longSetting = <T extends string>(
    label: string,
    value: T,
    options: { value: T; label: string; short?: string; title?: string }[],
    onChange: (value: T) => void,
  ) =>
    stacked ? (
      <div key={label} className="flex flex-col gap-2">
        <span className="text-sm font-medium text-mute">{label}</span>
        <div className="flex">
          <SlateSelect
            label={label}
            value={value}
            options={options}
            onChange={onChange}
            className="border border-line"
          />
        </div>
      </div>
    ) : (
      <SlateSelect key={label} label={label} value={value} options={options} onChange={onChange} />
    );

  if (kind === "image") {
    const dimensionOptions = (side: "width" | "height") =>
      imageDimensionOptions(image.aspect, side, image.model).map((pixels) => ({
        value: pixels,
        label: `${pixels} px`,
        short: String(pixels),
      }));
    const resolution = (
      <div
        title="Resolution"
        className={cn("flex items-center gap-1", stacked && "flex-col items-start gap-2")}
      >
        {stacked ? <span className="text-sm font-medium text-mute">Resolution</span> : null}
        <div className={cn("flex items-center gap-1", stacked && "rounded-[9px] border border-line")}>
          <SlateSelect
            label="Width"
            value={image.width}
            options={dimensionOptions("width")}
            onChange={(width) =>
              composer.setImage(imageSizeFromSide(image.aspect, "width", width, image.model))
            }
          />
          <span className="text-xs text-mute" aria-hidden>
            ×
          </span>
          <SlateSelect
            label="Height"
            value={image.height}
            options={dimensionOptions("height")}
            onChange={(height) =>
              composer.setImage(imageSizeFromSide(image.aspect, "height", height, image.model))
            }
          />
        </div>
      </div>
    );
    return (
      <>
        {longSetting(
          "Model",
          image.model,
          imageModels.map((model) => ({
            value: model,
            label: `${IMAGE_MODELS[model].label} · ${IMAGE_MODELS[model].note}`,
            short: IMAGE_MODELS[model].label,
            title: IMAGE_MODELS[model].note,
          })),
          (model) => {
            // a model keeps as many of the attached pictures as it can work from: none, or the first few
            const kept = image.references.slice(0, maxImageReferences(model));
            const dropped = image.references.length - kept.length;
            const resetsSize = image.width * image.height > IMAGE_MODEL_MAX_PIXELS[model];
            composer.setImage({
              model,
              references: kept,
              ...(resetsSize ? IMAGE_SIZES[image.aspect] : {}),
            });
            if (dropped > 0 && kept.length === 0)
              toast(`References removed: ${IMAGE_MODELS[model].label} only makes pictures from text.`);
            else if (dropped > 0)
              toast(
                `${IMAGE_MODELS[model].label} works from ${kept.length} pictures: the last ${dropped} were removed.`,
              );
          },
        )}
        {resolution}
        {setting(
          "Shape",
          image.aspect,
          (Object.keys(IMAGE_SIZES) as ImageAspect[]).map((a) => ({ value: a, label: a })),
          (aspect) => composer.setImage({ aspect, ...IMAGE_SIZES[aspect] }),
        )}
        {setting(
          "Variants",
          image.count,
          [1, 2, 3, 4].map((n) => ({
            value: n,
            label: stacked ? `×${n}` : n === 1 ? "1 image" : `${n} images`,
          })),
          (count) => composer.setImage({ count }),
        )}
        {imageFast
          ? setting("Speed", image.speed, speedOptions(stacked), (speed) => composer.setImage({ speed }))
          : null}
      </>
    );
  }

  if (kind === "video") {
    return (
      <>
        {setting("Quality", video.resolution, VIDEO_RESOLUTIONS, (resolution) =>
          composer.setVideo({ resolution }),
        )}
        {setting(
          "Shape",
          video.aspect,
          (["16:9", "9:16", "1:1"] as VideoAspect[]).map((a) => ({ value: a, label: a })),
          (aspect) => composer.setVideo({ aspect }),
        )}
        {setting(
          "Length",
          video.frames,
          VIDEO_FRAME_OPTIONS.map((f) => ({
            value: f,
            label: secondsLabel(f),
            disabled: f > maxFrames,
            title: f > maxFrames ? "Too long for this quality; choose a lower one" : undefined,
          })),
          (frames) => composer.setVideo({ frames }),
        )}
        {setting("Detail", video.quality, VIDEO_DETAIL, (quality) => composer.setVideo({ quality }))}
        {videoFast
          ? setting("Speed", video.speed, speedOptions(stacked), (speed) => composer.setVideo({ speed }))
          : null}
      </>
    );
  }

  if (kind === "speech") {
    return (
      <>
        {setting(
          "Voice",
          speech.mode,
          modes.map((m) => ({
            value: m,
            label: stacked ? SPEECH_MODE_SHORT[m] : SPEECH_MODE_LABELS[m].title,
            short: SPEECH_MODE_SHORT[m],
            title: SPEECH_MODE_LABELS[m].hint,
          })),
          (mode) => composer.setSpeech({ mode }),
        )}
        {speech.mode === "speaker"
          ? longSetting(
              "Speaker",
              speech.speaker,
              speakers.map((s) => ({
                value: s,
                label: `${SPEECH_SPEAKER_LABELS[s].title} · ${SPEECH_SPEAKER_LABELS[s].hint}`,
                short: SPEECH_SPEAKER_LABELS[s].title,
                title: SPEECH_SPEAKER_LABELS[s].hint,
              })),
              (speaker) => composer.setSpeech({ speaker }),
            )
          : null}
        {longSetting(
          "Language",
          speech.language,
          SpeechLanguage.options.map((l) => ({
            value: l,
            label: SPEECH_LANGUAGE_LABELS[l],
            title: l === "auto" ? "Read the language off the text" : undefined,
          })),
          (language) => composer.setSpeech({ language }),
        )}
      </>
    );
  }

  return (
    <>
      {setting("Length", music.durationSec, MUSIC_LENGTHS, (durationSec) =>
        composer.setMusic({ durationSec }),
      )}
      {setting(
        "Vocals",
        music.instrumental ? "inst" : music.withLyrics ? "lyrics" : "auto",
        MUSIC_VOCALS,
        (value) => composer.setMusic({ instrumental: value === "inst", withLyrics: value === "lyrics" }),
      )}
    </>
  );
}
